import React, {
  createContext,
  useMemo,
  useEffect,
  useCallback,
  useReducer,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { debounce, isEqual } from 'lodash'
import * as Sentry from '@sentry/react'

import { useImmerReducerAsync } from 'hooks/useReducerAsync'
import {
  AssetClass,
  Platform,
  validateCreative,
  validateIntegrations,
  SPECIAL_AD_CATEGORY,
} from 'api/models'
import { initStateObject, objectReducer, UPDATE } from 'reducers/default'
import {
  derivePlatformSelectionFromSetup,
  mapSetupPlatformToMultiValueQueryParam,
  platformIncludesMeta,
} from '../platform'
import {
  activeTargetingPlatforms,
  validateLocationTargeting,
} from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'
import { deriveCampaignTypeFromSetup } from 'components/Recommendations/EventCampaigns/Steps/Setup/Goals/campaignType'
import { TargetingContextProvider } from '../Edit/CampaignForm/Targeting/TargetingContext'
import useHasPermission, { Permissions } from 'hooks/useHasPermission'
import { offsetVariationsToHandleRemovedCreativeFields } from './disabledVariations'
import { codifyAPIError } from 'utils/helpers'
import metaAuthorizationApi from 'api/connect/metaAuth'
import { actions } from './actionTypes'
import { reducer } from './reducer'
import {
  asyncActionHandlers,
  createSetupAsync,
  deleteSetupAsync,
  editPublishedSetupAsync,
  enrichAccountsAndReconfigureAsync,
  enrichAccountsAsync,
  fetchAccountsAsync,
  fetchSetupAsync,
  publishSetupAsync,
  reconfigureSetupAsync,
  syncSetupAsync,
  updateTCTargetingAsync,
  updateTargetingAsync,
} from './asyncHandlers'
import {
  _checkDisabledVariationsIntegrity,
  applyAgeTargetingToTargeting,
  calculateSetupCreativesFromUpdatedTCCreatives,
  getAgeTargetingFromSetup,
  getDisabledVariationHashes,
  hasAudienceTargeting,
  hasSavedAudienceTargeting,
} from './util'
import { useSearchParam } from 'hooks/useSearchParams'
import { EUROPEAN_COUNTRIES } from 'components/Recommendations/EventCampaigns/Steps/Setup/constants'
/** @typedef {import('./types.js').SetupState} SetupState */
/** @typedef {import('./types.js').CampaignSetupDispatchContext} CampaignSetupDispatchContextType */

/**
 * @type {React.Context<SetupState>}
 */
export const CampaignSetupContext = createContext(null)
/**
 * @type {React.Context<CampaignSetupDispatchContextType>}
 */
export const CampaignSetupDispatchContext = createContext(null)
export const CurrentStepContext = createContext(null)
export const CurrentStepDispatchContext = createContext(null)

export function CampaignSetupStepProvider({ children }) {
  const searchParam = useSearchParam()
  const step = searchParam.get('step')
  const [currentStepState, dispatchCurrentStep] = useReducer(objectReducer, {
    ...initStateObject,
    content: {
      currentStep: step ? parseInt(step, 10) : 1,
      currentCompletedStep: null,
    },
  })

  const setCurrentStep = useCallback(
    (step, completedStep = null) => {
      const payload = completedStep ? { currentCompletedStep: completedStep } : {}
      dispatchCurrentStep({ type: UPDATE, payload: { ...payload, currentStep: step } })
    },
    [dispatchCurrentStep],
  )

  const goToNextStep = useCallback(() => {
    if (currentStepState.content.currentStep === 5) return

    const { currentStep, currentCompletedStep } = currentStepState.content
    setCurrentStep(currentStep + 1, currentCompletedStep + 1)
  }, [currentStepState, setCurrentStep])

  const goToPrevStep = useCallback(() => {
    if (currentStepState.content.currentStep === 1) return

    const { currentStep, currentCompletedStep } = currentStepState.content
    setCurrentStep(currentStep - 1, currentCompletedStep - 1)
  }, [currentStepState, setCurrentStep])

  return (
    <CurrentStepContext.Provider value={currentStepState.content}>
      <CurrentStepDispatchContext.Provider value={{ setCurrentStep, goToNextStep, goToPrevStep }}>
        {children}
      </CurrentStepDispatchContext.Provider>
    </CurrentStepContext.Provider>
  )
}

CampaignSetupStepProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default function CampaignSetupProvider({
  eventId,
  campaign,
  campaignSetup,
  children,
  anonScopes = null,
}) {
  const hasWaveAccess = useHasPermission(Permissions.wave)
  // Derived permission helpers: null anonScopes means regular user (no restrictions)
  const canAnonEdit = anonScopes === null || anonScopes.includes('setup_process:write')
  const canAnonBoost = anonScopes === null || anonScopes.includes('setup_process:boost')
  const isAnonUser = anonScopes !== null
  const initialState = useMemo(() => {
    if (!eventId) return null

    if (!campaign || !campaignSetup) {
      return {
        ..._initialState,
        eid: eventId,
      }
    }

    const creative = campaignSetup.creative

    return {
      ..._initialState,
      eid: eventId,
      isPublished: true,
      setup: {
        ...campaignSetup,
        creatives: [
          {
            ...creative,
            tc: campaign.tc,
            tc_run_id: campaign.tc_run_id,
          },
        ],
      },
    }
    // campaignSetup arrives via a separate async fetch (v4 campaign_setups)
    // than `campaign` (v3 campaigns list) in the one caller that publishes a
    // real one (RunningCampaigns.jsx) — that caller gates its render on the
    // fetch settling first, so by the time this mounts, campaignSetup is
    // already whatever it'll be for this mount (plain useReducer initial-value
    // semantics: this memo's value on first render is all that's used).
  }, [eventId, campaign, campaignSetup])

  const [state, dispatch] = useImmerReducerAsync(reducer, initialState, asyncActionHandlers)
  const { eid, setup, accounts, loading, componentErrors, hasPendingMedia, isPublished } = state
  // enrichAccountsAndReconfigure's own identity must stay stable across
  // unrelated state churn (loading flags, patchAccounts, etc.) — it's a
  // dependency of SelectAdPlatformForm's auto-default effects, and closing
  // over `state` directly (like this file's other setup-writing callbacks)
  // means those effects re-evaluate on every dispatch anywhere in this
  // context, not just on changes actually relevant to account defaulting.
  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])
  const shouldAutosync = useMemo(() => !campaignSetup, [campaignSetup])
  const [debouncedPending, setPendingDebounce] = useState(false)
  const disabledVariationsHashRef = useRef(isPublished ? getDisabledVariationHashes(setup) : {})

  useEffect(() => {
    if (!setup) {
      disabledVariationsHashRef.current = {}
      return
    }
  }, [setup])

  // `setup` only gets its initial value from `campaignSetup` once, when this
  // provider first mounts — that's just how useReducer's lazy init works
  // (same as `initialState` above). So when a published TC gets edited and
  // we refetch fresh data into `campaignSetup`, nothing carries it over into
  // `setup` on its own, and every consumer of useCampaignSetup().setup
  // (MediaUpload, Targeting, the flags below, ...) is left reading stale or
  // null data. This effect just watches for that and re-syncs `setup`
  // whenever `campaignSetup` actually changes, using the same reshape as
  // `initialState`.
  const lastSyncedCampaignSetupRef = useRef(campaignSetup)
  useEffect(() => {
    if (!campaign || !campaignSetup) return
    if (isEqual(lastSyncedCampaignSetupRef.current, campaignSetup)) return
    lastSyncedCampaignSetupRef.current = campaignSetup

    dispatch({
      type: actions.setupLoaded,
      payload: {
        ...campaignSetup,
        creatives: [
          {
            ...campaignSetup.creative,
            tc: campaign.tc,
            tc_run_id: campaign.tc_run_id,
          },
        ],
      },
    })
  }, [campaign, campaignSetup, dispatch])

  const fetchSetup = useCallback(() => {
    const action = { type: actions.fetchAsync, payload: { eid } }
    fetchSetupAsync({ dispatch })(action).then(({ success, setup }) => {
      if (success && setup) {
        disabledVariationsHashRef.current = getDisabledVariationHashes(setup)
      }
    })
  }, [dispatch, eid])

  const debouncedSync = useRef(
    debounce((setup, callback = () => {}) => {
      setPendingDebounce(false)
      if (!shouldAutosync) {
        return
      }

      const action = { type: actions.syncAsync, payload: { eid, setup } }
      syncSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    }, 1000),
  )

  const sync = useCallback(
    (setup, callback = () => {}) => {
      const action = { type: actions.syncAsync, payload: { setup } }
      syncSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  const startSetup = useCallback(
    (config, callback = () => {}) => {
      const action = {
        type: actions.createAsync,
        payload: { eid: state.eid, config },
      }
      createSetupAsync({ dispatch })(action).then(callback)
    },
    [dispatch, state],
  )

  // Keyed on the derived 'meta'|'google'|'both' string, not the raw
  // setup.platform array from the API response — that array gets a new
  // reference on every setup replacement (any save, not just a genuine
  // platform change), which would otherwise recompute this memo (and give
  // fetchAccounts a new identity) on every single setup save.
  const setupPlatformSelection = derivePlatformSelectionFromSetup(setup)
  const accountFetchPlatforms = useMemo(
    () => mapSetupPlatformToMultiValueQueryParam(setupPlatformSelection),
    [setupPlatformSelection],
  )

  const fetchAccounts = useCallback(() => {
    const action = { type: actions.fetchAccountsAsync }
    fetchAccountsAsync({ dispatch, platforms: accountFetchPlatforms })(action)
  }, [dispatch, anonScopes, accountFetchPlatforms])

  const enrichAccounts = useCallback(
    (accounts, callback) => {
      const action = { type: actions.validateAccountsAsync, payload: { accounts } }
      enrichAccountsAsync({ dispatch })(action).then(callback)
    },
    [dispatch],
  )

  const enrichAsset = useCallback(
    (asset) => {
      const { asset_id, asset_class, platform } = asset
      const account = accounts.find(
        (x) => x.asset_id === asset_id && x.asset_class === asset_class && x.platform === platform,
      )

      return {
        ...asset,
        ...account,
      }
    },
    [accounts],
  )

  const getAdAccount = useCallback(
    (platform) => {
      if (!setup) {
        return null
      }

      const setupPlatform = derivePlatformSelectionFromSetup(setup)
      const targetPlatform =
        platform ?? (platformIncludesMeta(setupPlatform) ? Platform.facebook : Platform.google)

      const selectedAdAccount = setup.integration_details?.find(
        (x) => x.asset_class === AssetClass.ad_account && x.platform === targetPlatform,
      )
      if (!selectedAdAccount) {
        return null
      }

      return enrichAsset(selectedAdAccount)
    },
    [setup, enrichAsset],
  )

  const enrichAccountsAndReconfigure = useCallback(
    (config, callback = () => {}) => {
      const action = {
        type: actions.enrichAccountsAndReconfigureAsync,
        payload: { eid: stateRef.current.eid, config },
      }
      enrichAccountsAndReconfigureAsync({ dispatch, getState: () => stateRef.current })(
        action,
      ).then(callback)
    },
    [dispatch],
  )

  const reconfigureSetup = useCallback(
    (config, callback = () => {}) => {
      const action = {
        type: actions.reconfigureAsync,
        payload: { eid: state.eid, config },
      }
      reconfigureSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  // Cancels a pending 1s-debounced per-TC autosync (debouncedSync above) —
  // callers that write through to setup_process immediately (e.g.
  // CampaignSetup.jsx's platform/campaign-type switch) must call this first,
  // otherwise a stale debounced write scheduled before the switch can still
  // fire afterward with a pre-switch `setup` snapshot and clobber it.
  const cancelPendingSync = useCallback(() => {
    debouncedSync.current.cancel()
  }, [])

  const cancelSetup = useCallback(
    (callback = () => {}) => {
      const action = {
        type: actions.deleteAsync,
        payload: { eid: state.eid },
      }
      deleteSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  const setHasPendingMedia = useCallback(
    (hasPendingMedia) => {
      dispatch({ type: actions.setHasPendingMedia, payload: hasPendingMedia })
    },
    [dispatch],
  )

  const overwriteTCCreatives = useCallback(
    ({ tc, tc_run_id, creatives, hasPendingMedia = false, creativeAction }) => {
      const { removedFields } = creativeAction
      if (removedFields) {
        creatives.excluded_variations = offsetVariationsToHandleRemovedCreativeFields(
          removedFields,
          setup.creatives,
          tc,
        )
      }

      dispatch({
        type: actions.setTCCreatives,
        payload: { tc, tc_run_id, hasPendingMedia, ...creatives },
      })

      const newCreatives = calculateSetupCreativesFromUpdatedTCCreatives(
        setup.creatives,
        tc,
        tc_run_id,
        creatives,
      )

      if (shouldAutosync && !hasPendingMedia) {
        debouncedSync.current.cancel()
        setPendingDebounce(true)
        debouncedSync.current({ ...setup, creatives: newCreatives })
      }
    },
    [dispatch, setup, shouldAutosync],
  )

  const updateDisabledAdVariations = useCallback(
    (disabledVariations, callback = () => {}, autoSync = true) => {
      const creatives = setup.creatives.map((creative) => ({
        ...creative,
        excluded_variations: disabledVariations[creative.tc],
      }))

      dispatch({
        type: actions.setCreatives,
        payload: creatives,
      })

      if (autoSync) {
        sync({ ...setup, creatives }, ({ success, data }) => {
          if (success) {
            disabledVariationsHashRef.current = getDisabledVariationHashes({ ...setup, creatives })
          }
          callback({ success, data })
        })
      }
    },
    [dispatch, setup, sync],
  )

  const overwriteTCTargeting = useCallback(
    (tcTargeting, callback = () => {}, isFirstTc = false, skipNetworkSync = false) => {
      const action = {
        type: actions.updateTCTargetingAsync,
        payload: { ...tcTargeting, shouldSync: !isPublished && !skipNetworkSync, isFirstTc },
      }

      updateTCTargetingAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state, isPublished],
  )

  const updateAgeTargeting = useCallback(
    (min, max, callback = () => {}) => {
      const platforms = activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup))
      const targeting = applyAgeTargetingToTargeting(setup.targeting, platforms, min, max)
      const action = {
        type: actions.updateTargetingAsync,
        payload: { targeting, shouldSync: !isPublished },
      }
      updateTargetingAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state, isPublished, setup],
  )

  const checkDisabledVariationsIntegrity = useCallback(
    (currentDisabledVariations = null) => {
      const creatives = setup.creatives
      const integrityHashes = disabledVariationsHashRef.current

      return _checkDisabledVariationsIntegrity(
        creatives,
        integrityHashes,
        currentDisabledVariations,
      )
    },
    [setup],
  )

  const updateDsaFields = useCallback(
    (fields, callback = () => {}) => {
      if (!setup) return

      const nextSetup = {
        ...setup,
        ...(fields.dsa_payor !== undefined ? { dsa_payor: fields.dsa_payor } : {}),
        ...(fields.dsa_beneficiary !== undefined
          ? { dsa_beneficiary: fields.dsa_beneficiary }
          : {}),
      }

      // Optimistically update local state so UI reflects latest values
      dispatch({ type: actions.setupLoaded, payload: nextSetup })

      // Sync with delay
      if (shouldAutosync) {
        debouncedSync.current.cancel()
        setPendingDebounce(true)
        debouncedSync.current(nextSetup, callback)
      }
    },
    [setup, dispatch, shouldAutosync],
  )

  const publishSetup = useCallback(
    (callback = () => {}) => {
      // force re-validation by fetching the setup
      let action = { type: actions.fetchAsync, payload: { eid } }
      fetchSetupAsync({ dispatch })(action).then(({ success, setup, notFound }) => {
        if (notFound || (success && !setup)) {
          callback({ success: false, reason: 'SETUP_NOT_FOUND' })
          return
        }

        if (success) {
          enrichAccountsAsync({ dispatch })({
            type: actions.validateAccountsAsync,
            payload: { accounts: setup.integration_details },
          }).then(({ success, hasInvalidAccounts }) => {
            if (success && !hasInvalidAccounts) {
              const isIntegrationsValid = validateIntegrations(
                setup.integration_details,
                accounts,
                derivePlatformSelectionFromSetup(setup),
              )
              const isCreativesValid = setup.creatives.every(
                (x) =>
                  validateCreative(x, {
                    setupOnly: false,
                    platform: derivePlatformSelectionFromSetup(setup),
                    campaignType: deriveCampaignTypeFromSetup(setup),
                  }).valid,
              )
              const isTargetingValid = setup.targeting.every(
                (x) =>
                  validateLocationTargeting(
                    setup.targeting,
                    x.tc,
                    x.tc_run_id,
                    hasAudienceTargeting(x),
                    hasSavedAudienceTargeting(x),
                    activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
                    setup,
                  ).valid,
              )

              if (isIntegrationsValid && isCreativesValid && isTargetingValid) {
                publishSetupAsync({ dispatch, getState: () => state })().then(callback)
              } else {
                Sentry.captureMessage('Invalid setup', {
                  extra: {
                    eid,
                    isIntegrationsValid,
                    isCreativesValid,
                    isTargetingValid,
                  },
                })

                callback({ success: false, reason: 'INVALID_SETUP' })
              }
            } else {
              callback({ success: false, reason: 'INVALID_ACCOUNTS' })
            }
          })
        }
      })
    },
    [dispatch, state, eid, accounts],
  )

  const editPublishedSetup = useCallback(
    (callback = () => {}, disabledVariations = null, isFirstTc = false) => {
      const action = {
        type: actions.editAsync,
        payload: { tc: campaign.tc, tc_run_id: campaign.tc_run_id, disabledVariations, isFirstTc },
      }
      editPublishedSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state, campaign],
  )

  const setError = useCallback(
    (error) => {
      dispatch({ type: actions.error, payload: error })
    },
    [dispatch],
  )

  const setComponentError = useCallback(
    (key, error) => {
      dispatch({ type: actions.setComponentError, payload: { key, error } })
    },
    [dispatch],
  )

  const getAgeTargeting = useCallback(() => {
    const platforms = activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup))
    return getAgeTargetingFromSetup(setup, platforms)
  }, [setup])

  const isAgeRangeDisabled = useCallback(
    (event) => {
      if (!event || !event.special_ad_category) {
        return false
      }

      const isEuropeanCountry = (() => {
        if (!setup?.targeting || !Array.isArray(setup.targeting) || setup.targeting.length === 0) {
          return false
        }

        const allCountryCodes = new Set()

        setup.targeting.forEach((tcTargeting) => {
          ;['fb', 'google'].forEach((platform) => {
            const platformTargeting = tcTargeting[platform]
            if (!platformTargeting) return

            if (Array.isArray(platformTargeting.range_locations?.included)) {
              platformTargeting.range_locations.included.forEach((locationItem) => {
                if (locationItem?.location?.country_code) {
                  allCountryCodes.add(locationItem.location.country_code)
                }
              })
            }

            if (
              platformTargeting.postal_codes?.countries &&
              typeof platformTargeting.postal_codes.countries === 'object'
            ) {
              Object.keys(platformTargeting.postal_codes.countries).forEach((countryCode) => {
                allCountryCodes.add(countryCode)
              })
            }
          })
        })

        if (allCountryCodes.size === 0) {
          return false
        }

        return Array.from(allCountryCodes).every((countryCode) =>
          EUROPEAN_COUNTRIES.includes(countryCode),
        )
      })()

      // Age range is disabled if:
      // 1. Event has employment or housing special ad category
      // 2. Event has financial or political special ad category AND targeting is not European
      return (
        event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.employment) ||
        event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.housing) ||
        (!isEuropeanCountry &&
          event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.financial)) ||
        (!isEuropeanCountry && event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.political))
      )
    },
    [setup],
  )

  const isGeneratingForCluster = useCallback(
    (tc, tc_run_id) => {
      const creative = setup?.creatives?.find((c) => c.tc === tc && c.tc_run_id === tc_run_id)
      if (!creative) return false

      return !!(
        creative.media?.some((m) => m.generating) ||
        creative.placement_media?.some((m) => m.generating)
      )
    },
    [setup],
  )

  const isGeneratingValid = useCallback(() => {
    if (!setup?.creatives?.length) return true

    return !setup.creatives.some((creative) =>
      isGeneratingForCluster(creative.tc, creative.tc_run_id),
    )
  }, [setup, isGeneratingForCluster])

  const isTargetingValid = useMemo(() => {
    if (!Array.isArray(setup?.targeting)) return false

    const targeting = setup.targeting

    const platforms = activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup))

    return targeting.every(
      (x) =>
        validateLocationTargeting(
          targeting,
          x.tc,
          x.tc_run_id,
          hasAudienceTargeting(x),
          hasSavedAudienceTargeting(x),
          platforms,
          setup,
        ).valid,
    )
  }, [setup])

  const isIntegrationsValid = useMemo(() => {
    if (Object.values(componentErrors).some((x) => x)) {
      return false
    }
    return (
      setup &&
      validateIntegrations(
        setup.integration_details,
        accounts,
        derivePlatformSelectionFromSetup(setup),
      )
    )
  }, [setup, accounts, componentErrors])

  const isCreativesValid = useMemo(
    () =>
      setup &&
      setup.creatives.every(
        (x) =>
          validateCreative(x, {
            setupOnly: false,
            platform: derivePlatformSelectionFromSetup(setup),
            campaignType: deriveCampaignTypeFromSetup(setup),
          }).valid,
      ),
    [setup],
  )

  const isClusterValid = useCallback(
    (tc, tc_run_id) => {
      if (!setup || !setup.creatives) return false

      const creative = setup.creatives.find((x) => x.tc === tc && x.tc_run_id === tc_run_id)
      if (!creative) return false

      const isCreativeValid = validateCreative(creative, {
        setupOnly: false,
        platform: derivePlatformSelectionFromSetup(setup),
        campaignType: deriveCampaignTypeFromSetup(setup),
      }).valid
      if (!isCreativeValid) return false

      if (!Array.isArray(setup.targeting)) return false

      const tcTargeting = setup.targeting.find((x) => x.tc === tc && x.tc_run_id === tc_run_id)
      const isTargetingValid = validateLocationTargeting(
        setup.targeting,
        tc,
        tc_run_id,
        hasAudienceTargeting(tcTargeting),
        hasSavedAudienceTargeting(tcTargeting),
        activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
        setup,
      ).valid

      if (!isTargetingValid) return false

      if (isGeneratingForCluster(tc, tc_run_id)) return false

      return true
    },
    [setup, isGeneratingForCluster],
  )

  useEffect(() => {
    if (!setup) {
      return
    }

    const disabledVariations = setup.creatives.reduce((acc, c) => {
      acc[c.tc] = c.excluded_variations || []
      return acc
    }, {})

    if (!isEqual(disabledVariations, state.disabledVariations)) {
      dispatch({ type: actions.setSetupDisabledVariations, payload: disabledVariations })
    }
  }, [setup, dispatch, state.disabledVariations])

  const isStepValid = useCallback(
    (step) => {
      // in anon readonly mode, all steps are valid
      if (isAnonUser && !canAnonEdit) {
        return true
      }
      switch (step) {
        case 1:
          return true
        case 2:
          return isIntegrationsValid && !loading
        case 3:
          return (
            isIntegrationsValid &&
            isCreativesValid &&
            isTargetingValid &&
            isGeneratingValid() &&
            !hasPendingMedia &&
            !debouncedPending &&
            !loading
          )
        case 4:
          return (
            isIntegrationsValid &&
            isCreativesValid &&
            isTargetingValid &&
            (hasWaveAccess || canAnonBoost) &&
            !loading &&
            !debouncedPending
          )
        default:
          return false
      }
    },
    [
      hasWaveAccess,
      canAnonBoost,
      isIntegrationsValid,
      isCreativesValid,
      isTargetingValid,
      isGeneratingValid,
      hasPendingMedia,
      debouncedPending,
      loading,
    ],
  )

  useEffect(() => {
    const fetchMetaStatus = async () => {
      const resp = await metaAuthorizationApi.getStatus()
      if (resp.error) {
        setError(codifyAPIError(resp))
      }

      const { partner_connection_type } = resp.data ?? {}
      dispatch({
        type: actions.setMetaStatus,
        payload: partner_connection_type,
      })
    }

    fetchMetaStatus()
  }, [dispatch, setError])

  useEffect(() => {
    if (eventId && !campaign) {
      fetchSetup()
    }
  }, [eventId, fetchSetup, campaign])

  useEffect(() => {
    dispatch({ type: actions.setCreativesValid, payload: isCreativesValid })
  }, [dispatch, isCreativesValid])

  useEffect(() => {
    dispatch({ type: actions.setTargetingValid, payload: isTargetingValid })
  }, [dispatch, isTargetingValid])

  useEffect(() => {
    dispatch({ type: actions.setIntegrationsValid, payload: isIntegrationsValid })
  }, [dispatch, isIntegrationsValid])

  return (
    <CampaignSetupStepProvider>
      <CampaignSetupContext.Provider
        value={{ ...state, anonScopes, canAnonEdit, canAnonBoost, isAnonUser }}
      >
        <CampaignSetupDispatchContext.Provider
          value={{
            dispatch,
            fetchSetup,
            startSetup,
            cancelSetup,
            checkDisabledVariationsIntegrity,
            publishSetup,
            isStepValid,
            reconfigureSetup,
            cancelPendingSync,
            overwriteTCCreatives,
            overwriteTCTargeting,
            updateDisabledAdVariations,
            editPublishedSetup,
            setHasPendingMedia,
            fetchAccounts,
            enrichAccountsAndReconfigure,
            enrichAccounts,
            enrichAsset,
            getAdAccount,
            setError,
            setComponentError,
            getAgeTargeting,
            updateAgeTargeting,
            isAgeRangeDisabled,
            isClusterValid,
            isGeneratingForCluster,
            isGeneratingValid,
            updateDsaFields,
          }}
        >
          <TargetingContextProvider>{children}</TargetingContextProvider>
        </CampaignSetupDispatchContext.Provider>
      </CampaignSetupContext.Provider>
    </CampaignSetupStepProvider>
  )
}

CampaignSetupProvider.propTypes = {
  eventId: PropTypes.string.isRequired,
  campaign: PropTypes.shape({
    tc: PropTypes.string.isRequired,
    tc_run_id: PropTypes.number.isRequired,
  }),
  // v4 replacement for the old campaign.latest_campaign_setup — same
  // {integration_details, creative, targeting} shape, sourced from
  // publishedCampaignSetupsApi.fetchCampaignSetupsAsync via
  // hooks/usePublishedCampaignSetups's toCampaignSetupCompat.
  campaignSetup: PropTypes.shape({
    integration_details: PropTypes.array,
    creative: PropTypes.object,
    targeting: PropTypes.array,
  }),
  anonScopes: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired,
}

export const useCampaignSetup = () => {
  const context = React.useContext(CampaignSetupContext)

  if (context === undefined) {
    throw new Error('useCampaignSetup must be used within a CampaignSetupProvider')
  }

  return context
}

export const useCampaignSetupDispatch = () => {
  const context = React.useContext(CampaignSetupDispatchContext)

  if (context === undefined) {
    throw new Error('useCampaignSetupDispatch must be used within a CampaignSetupProvider')
  }

  return context
}

export const useCurrentStep = () => {
  const context = React.useContext(CurrentStepContext)

  if (context === undefined) {
    throw new Error('useCurrentStep must be used within a CampaignSetupProvider')
  }

  return context
}

export const useCurrentStepDispatch = () => {
  const context = React.useContext(CurrentStepDispatchContext)

  if (context === undefined) {
    throw new Error('useCurrentStepDispatch must be used within a CampaignSetupProvider')
  }

  return context
}

const _initialState = {
  loading: false,
  hasPendingMedia: false,
  error: null,
  componentErrors: {}, // used to store errors for specific components
  lastSyncedAt: null,
  eid: null,
  setup: null,
  setupNotFound: false,
  isCreativesValid: false,
  isIntegrationsValid: false,
  isTargetingValid: false,
  disabledVariations: {},
  accounts: [],
  accountsLoaded: false,
  metaConnectionStatus: null,
  metaStatusLoaded: false,
}
